import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import seoConfig from '../config/seo.config.json';
import about from '../data/aboutPage.json';
import caseStudies from '../data/caseStudies.json';
import resources from '../data/resources.json';
import services from '../data/servicePages.json';
import { useLanguage } from '../contexts/LanguageContext';
import { localizeContentItem } from '../data/contentLibrary';
import { FOUNDER_PROFILE } from '../config/founder';

const normalizePath = (pathname) => {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/, '') || '/';
};

const absoluteUrl = (path) =>
  path.startsWith('http') ? path : `${seoConfig.site.url}${path}`;

const setMeta = (selector, attribute, value) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attribute).forEach(([name, content]) => {
    element.setAttribute(name, content);
  });
  element.setAttribute('content', value);
};

const setCanonical = (url) => {
  let canonical = document.head.querySelector('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }
  canonical.setAttribute('href', url);
};

const defaultRouteMetadata = (path) => {
  const routeName = path
    .split('/')
    .filter(Boolean)
    .map((part) => part.replace(/-/g, ' '))
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' - ');
  return {
    title: routeName
      ? `${routeName} | ${seoConfig.site.name}`
      : seoConfig.site.defaultTitle,
    description: seoConfig.site.defaultDescription,
    type: 'WebPage',
  };
};

const publicServices = services.filter((service) => service.status === 'published');
const publicResources = resources.filter((resource) => resource.status === 'published');
const publicCaseStudies = caseStudies.filter(
  (caseStudy) =>
    caseStudy.publicStatus === 'published' &&
    caseStudy.publicationApproved === true
);

const breadcrumb = (...items) => items.map(([name, path]) => ({ name, path }));

const labelsForLanguage = (language) =>
  language === 'tr'
    ? {
        home: 'Ana Sayfa',
        about: 'Hakkımızda',
        services: 'Hizmetler',
        resources: 'Kaynaklar',
        work: 'Çalışmalar',
        serviceType: 'İşletme web sitesi ve dijital hizmet',
        workTitle: 'Webouse Çalışmaları',
      }
    : {
        home: 'Home',
        about: 'About',
        services: 'Services',
        resources: 'Resources',
        work: 'Work',
        serviceType: 'Business website and digital service',
        workTitle: 'Webouse Work',
      };

const serviceMetadata = (service, language) => {
  const labels = labelsForLanguage(language);
  return {
  title: service.metaTitle,
  description: service.metaDescription,
  type: 'WebPage',
  schema: {
    serviceName: service.title,
    serviceType: labels.serviceType,
    breadcrumbs: breadcrumb(
      [labels.home, '/'],
      [labels.services, '/services'],
      [service.title, `/services/${service.slug}`]
    ),
    faq: service.faq,
  },
  };
};

const resourceMetadata = (resource, language) => {
  const labels = labelsForLanguage(language);
  return {
  title: resource.metaTitle,
  description: resource.metaDescription,
  type: 'WebPage',
  schema: {
    article: {
      headline: resource.title,
      author: resource.author,
      datePublished: resource.publishDate,
      dateModified: resource.lastUpdated,
    },
    breadcrumbs: breadcrumb(
      [labels.home, '/'],
      [labels.resources, '/resources'],
      [resource.title, `/resources/${resource.slug}`]
    ),
  },
  };
};

const caseStudyMetadata = (caseStudy, language) => {
  const labels = labelsForLanguage(language);
  return {
  title: caseStudy.metaTitle || `${caseStudy.title} | ${labels.workTitle}`,
  description: caseStudy.metaDescription || caseStudy.summary || caseStudy.problem,
  image: caseStudy.ogImage,
  imageAlt: `${caseStudy.title} project preview`,
  type: 'WebPage',
  schema: {
    creativeWork: {
      name: caseStudy.title,
      creator: caseStudy.author,
      dateModified: caseStudy.lastUpdated,
      about: caseStudy.industry,
      image: caseStudy.ogImage,
    },
    breadcrumbs: breadcrumb(
      [labels.home, '/'],
      [labels.work, '/work'],
      [caseStudy.title, `/work/${caseStudy.slug}`]
    ),
  },
  };
};

const turkishStaticRouteMetadata = {
  '/services': {
    title: 'Web Sitesi Hizmetleri | Webouse',
    description:
      'Webouse hizmetleri; özel web tasarımı, redesign, dönüşüm odaklı sayfalar, klinik web sitesi tasarımı, randevu akışları ve SEO/GEO-ready geliştirmeyi kapsar.',
    type: 'WebPage',
  },
  '/resources': {
    title: 'Web Sitesi Kaynakları | Webouse',
    description:
      'SEO, GEO, AEO, randevu yolculukları, klinik web sitesi yapısı ve redesign kararları için pratik Webouse kaynakları.',
    type: 'WebPage',
  },
  '/work': {
    title: 'Webouse Çalışmaları | Kurum İçi Projeler ve Konseptler',
    description:
      'Webouse tarafından kurum içi girişimler ve yetenek gösterimleri için tasarlanıp geliştirilen sistemleri inceleyin.',
    type: 'CollectionPage',
  },
  '/how-it-works': {
    title: 'Webouse Nasıl Çalışır | Strateji, Tasarım ve Yayına Alma',
    description:
      'Webouse proje sürecinde keşif, kapsamlandırma, tasarım, geliştirme, test ve yayına alma adımlarının nasıl ilerlediğini öğrenin.',
    type: 'WebPage',
  },
  '/pricing': {
    title: 'Webouse Fiyatlandırma | Paketler ve Modüller',
    description:
      'Webouse paketleri, modülleri, aylık planları ve proje kapsamına göre değişen fiyatlandırma yaklaşımını inceleyin.',
    type: 'WebPage',
  },
  '/modules': {
    title: 'Webouse Modülleri | Web Sitesi ve Otomasyon Eklentileri',
    description:
      'Webouse modülleriyle randevu, lead akışı, içerik yapısı, SEO/GEO altyapısı ve iş süreçleri için ek özellikleri seçin.',
    type: 'WebPage',
  },
  '/plans': {
    title: 'Webouse Planları | Bakım ve Büyüme Desteği',
    description:
      'Webouse aylık planları; bakım, güncelleme, performans takibi ve büyüme desteği seçeneklerini kapsar.',
    type: 'WebPage',
  },
  '/contact': {
    title: 'Webouse ile İletişime Geç | Projenizi Görüşelim',
    description:
      'Webouse ile web sitesi hedeflerinizi, mevcut sorunlarınızı ve ihtiyacınız olan iş sistemi kapsamını paylaşın.',
    type: 'ContactPage',
  },
  '/book-call': {
    title: 'Danışmanlık Görüşmesi Planla | Webouse',
    description:
      'Webouse ile web sitesi hedeflerinizi, gereken modülleri ve proje kapsamını görüşmek için danışmanlık akışını başlatın.',
    type: 'ContactPage',
  },
  '/privacy': {
    title: 'Gizlilik Politikası | Webouse',
    description:
      'Webouse’un web sitesi, iletişim akışları, admin araçları ve private demo erişimi üzerinden bilgileri nasıl ele aldığını öğrenin.',
    type: 'WebPage',
  },
  '/terms': {
    title: 'Hizmet Şartları | Webouse',
    description:
      'Webouse hizmetlerinin genel online sunumu, proje kapsamı, demolar, içerik sorumluluğu ve sonuç garantisi sınırları.',
    type: 'WebPage',
  },
  '/cookies': {
    title: 'Çerez Politikası | Webouse',
    description:
      'Webouse’un zorunlu çerezler, demo session cookie’leri ve yerel dil/fiyat tercihleriyle ilgili yaklaşımı.',
    type: 'WebPage',
  },
};

const dynamicRouteMetadata = (path, language = 'en') => {
  const labels = labelsForLanguage(language);
  if (language === 'tr' && turkishStaticRouteMetadata[path]) {
    return turkishStaticRouteMetadata[path];
  }

  if (path === '/about') {
    const localizedAbout = localizeContentItem(about, language);
    return {
      title: localizedAbout.metaTitle,
      description: localizedAbout.metaDescription,
      type: 'AboutPage',
      schema: {
        organization: true,
        person: true,
        breadcrumbs: breadcrumb([labels.home, '/'], [labels.about, '/about']),
      },
    };
  }

  const serviceMatch = path.match(/^\/services\/([^/]+)$/);
  if (serviceMatch) {
    const service = publicServices.find((item) => item.slug === serviceMatch[1]);
    return service ? serviceMetadata(localizeContentItem(service, language), language) : null;
  }

  const resourceMatch = path.match(/^\/resources\/([^/]+)$/);
  if (resourceMatch) {
    const resource = publicResources.find((item) => item.slug === resourceMatch[1]);
    return resource ? resourceMetadata(localizeContentItem(resource, language), language) : null;
  }

  const caseStudyMatch = path.match(/^\/work\/([^/]+)$/);
  if (caseStudyMatch) {
    const caseStudy = publicCaseStudies.find((item) => item.slug === caseStudyMatch[1]);
    return caseStudy ? caseStudyMetadata(localizeContentItem(caseStudy, language), language) : null;
  }

  return null;
};

const notFoundMetadata = {
  title: `Page not found | ${seoConfig.site.name}`,
  description:
    'This Webouse page does not exist or may have moved. Use the main navigation to find pricing, modules, plans, industries or contact options.',
  type: 'WebPage',
};

const buildRouteSchema = (
  metadata,
  canonicalUrl,
  { includeBusiness = true, language = 'en' } = {}
) => {
  const organizationId = `${seoConfig.site.url}/#organization`;
  const websiteId = `${seoConfig.site.url}/#website`;
  const basePage = {
    '@type': metadata.type || 'WebPage',
    '@id': `${canonicalUrl}#webpage`,
    name: metadata.title,
    description: metadata.description,
    url: canonicalUrl,
    inLanguage: language,
    ...(includeBusiness
      ? {
          isPartOf: {
            '@id': websiteId,
          },
        }
      : {}),
  };
  const graph = [basePage];
  const schema = metadata.schema || {};

  if (includeBusiness) {
    graph.push(
      {
        '@type': ['Organization', 'LocalBusiness', 'ProfessionalService'],
        '@id': organizationId,
        name: seoConfig.site.name,
        description: seoConfig.site.defaultDescription,
        url: `${seoConfig.site.url}/`,
        logo: {
          '@type': 'ImageObject',
          url: absoluteUrl(seoConfig.site.logo),
          width: 512,
          height: 512,
        },
        email: about.contact.supportEmail,
        telephone: about.contact.phoneDisplay,
        areaServed: about.markets,
        founder: {
          '@type': 'Person',
          name: about.founder.name,
          jobTitle: about.founder.role,
          image: absoluteUrl(FOUNDER_PROFILE.image),
          sameAs: [FOUNDER_PROFILE.linkedIn, FOUNDER_PROFILE.instagram, FOUNDER_PROFILE.aeroCore],
        },
        contactPoint: {
          '@type': 'ContactPoint',
          contactType: 'customer support',
          email: about.contact.supportEmail,
          telephone: about.contact.phoneDisplay,
          availableLanguage: ['English', 'Turkish'],
        },
        sameAs: about.contact.instagram ? [about.contact.instagram] : undefined,
      },
      {
        '@type': 'WebSite',
        '@id': websiteId,
        name: seoConfig.site.name,
        url: `${seoConfig.site.url}/`,
        inLanguage: language,
        publisher: {
          '@id': organizationId,
        },
      }
    );
  }

  if (schema.serviceName) {
    graph.push({
      '@type': 'Service',
      name: schema.serviceName,
      serviceType: schema.serviceType || 'Business website and automation system',
      description: metadata.description,
      url: canonicalUrl,
      provider: {
        '@id': organizationId,
      },
      areaServed: schema.areaServed || 'Worldwide',
      inLanguage: language,
    });
  }

  if (schema.person) {
    graph.push({
      '@type': 'Person',
      name: about.founder.name,
      jobTitle: about.founder.role,
      worksFor: {
        '@id': organizationId,
      },
      image: absoluteUrl(FOUNDER_PROFILE.image),
      sameAs: [FOUNDER_PROFILE.linkedIn, FOUNDER_PROFILE.instagram, FOUNDER_PROFILE.aeroCore],
    });
  }

  if (schema.article) {
    graph.push({
      '@type': 'Article',
      headline: schema.article.headline,
      description: metadata.description,
      url: canonicalUrl,
      author: {
        '@type': 'Person',
        name: schema.article.author,
      },
      publisher: {
        '@id': organizationId,
      },
      datePublished: schema.article.datePublished,
      dateModified: schema.article.dateModified,
      inLanguage: language,
    });
  }

  if (schema.creativeWork) {
    graph.push({
      '@type': 'CreativeWork',
      name: schema.creativeWork.name,
      description: metadata.description,
      url: canonicalUrl,
      about: schema.creativeWork.about,
      creator: schema.creativeWork.creator,
      image: schema.creativeWork.image
        ? absoluteUrl(schema.creativeWork.image)
        : undefined,
      dateModified: schema.creativeWork.dateModified,
    });
  }

  if (Array.isArray(schema.breadcrumbs) && schema.breadcrumbs.length) {
    graph.push({
      '@type': 'BreadcrumbList',
      itemListElement: schema.breadcrumbs.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: absoluteUrl(item.path),
      })),
    });
  }

  if (Array.isArray(schema.faq) && schema.faq.length) {
    graph.push({
      '@type': 'FAQPage',
      mainEntity: schema.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    });
  }

  if (graph.length === 1) {
    return {
      '@context': 'https://schema.org',
      ...basePage,
    };
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
};

const SeoManager = () => {
  const { pathname } = useLocation();
  const { currentLang } = useLanguage();

  useEffect(() => {
    const path = normalizePath(pathname);
    const isAdmin = path === '/admin' || path.startsWith('/admin/');
    const isPrivateRoute = isAdmin || path === '/demo';
    const isPortfolioRoute = path === '/work' || path.startsWith('/work/');
    const metadataForPath =
      dynamicRouteMetadata(path, currentLang) || seoConfig.routes[path] || null;
    const isKnownRoute = Boolean(metadataForPath) || isPrivateRoute;
    const isNotFound = !isKnownRoute;
    const metadata = isNotFound
      ? notFoundMetadata
      : metadataForPath || defaultRouteMetadata(path);
    const canonicalUrl = `${seoConfig.site.url}${path === '/' ? '/' : path}`;
    const imageUrl = absoluteUrl(metadata.image || seoConfig.site.ogImage);
    const imageAlt = metadata.imageAlt || `${metadata.title} — ${seoConfig.site.name}`;
    const openGraphLocale = currentLang === 'tr' ? 'tr_TR' : seoConfig.site.locale;
    const alternateOpenGraphLocale = currentLang === 'tr' ? seoConfig.site.locale : 'tr_TR';
    const openGraphType = metadata.schema?.article ? 'article' : 'website';

    document.title = metadata.title;
    setCanonical(canonicalUrl);
    setMeta('meta[name="description"]', { name: 'description' }, metadata.description);
    setMeta(
      'meta[name="robots"]',
      { name: 'robots' },
      isPrivateRoute || isNotFound
        ? 'noindex, nofollow, noarchive'
        : 'index, follow, max-image-preview:large'
    );
    setMeta('meta[property="og:title"]', { property: 'og:title' }, metadata.title);
    setMeta(
      'meta[property="og:description"]',
      { property: 'og:description' },
      metadata.description
    );
    setMeta('meta[property="og:url"]', { property: 'og:url' }, canonicalUrl);
    setMeta('meta[property="og:type"]', { property: 'og:type' }, openGraphType);
    setMeta(
      'meta[property="og:site_name"]',
      { property: 'og:site_name' },
      seoConfig.site.name
    );
    setMeta('meta[property="og:locale"]', { property: 'og:locale' }, openGraphLocale);
    setMeta(
      'meta[property="og:locale:alternate"]',
      { property: 'og:locale:alternate' },
      alternateOpenGraphLocale
    );
    setMeta('meta[property="og:image"]', { property: 'og:image' }, imageUrl);
    setMeta('meta[property="og:image:width"]', { property: 'og:image:width' }, '1200');
    setMeta('meta[property="og:image:height"]', { property: 'og:image:height' }, '630');
    setMeta('meta[property="og:image:alt"]', { property: 'og:image:alt' }, imageAlt);
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card' }, 'summary_large_image');
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title' }, metadata.title);
    setMeta(
      'meta[name="twitter:description"]',
      { name: 'twitter:description' },
      metadata.description
    );
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image' }, imageUrl);
    setMeta('meta[name="twitter:image:alt"]', { name: 'twitter:image:alt' }, imageAlt);

    if (process.env.REACT_APP_GOOGLE_SITE_VERIFICATION) {
      setMeta(
        'meta[name="google-site-verification"]',
        { name: 'google-site-verification' },
        process.env.REACT_APP_GOOGLE_SITE_VERIFICATION
      );
    }

    if (process.env.REACT_APP_BING_SITE_VERIFICATION) {
      setMeta(
        'meta[name="msvalidate.01"]',
        { name: 'msvalidate.01' },
        process.env.REACT_APP_BING_SITE_VERIFICATION
      );
    }

    let routeSchema = document.getElementById('webouse-route-schema');
    if (!routeSchema) {
      routeSchema = document.createElement('script');
      routeSchema.id = 'webouse-route-schema';
      routeSchema.type = 'application/ld+json';
      document.head.appendChild(routeSchema);
    }
    routeSchema.textContent = JSON.stringify(
      buildRouteSchema(metadata, canonicalUrl, {
        includeBusiness: !isPrivateRoute && !isNotFound && !isPortfolioRoute,
        language: currentLang,
      })
    );
  }, [currentLang, pathname]);

  return null;
};

export default SeoManager;
